import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const About = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const objectives = t("about.objectives.items", { returnObjects: true });
  const targetGroups = t("about.targetGroups.items", { returnObjects: true });

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative py-20 overflow-hidden bg-gradient-to-br from-emerald-100 via-emerald-200 to-teal-300"
    >
      {/* Creative Shapes Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large curved shape top right */}
        <div className="absolute -top-20 -right-40 w-96 h-96 bg-emerald-400 rounded-full opacity-60"></div>

        {/* Decorative shapes */}
        <div className="absolute top-32 right-20 w-40 h-40 bg-teal-400 rounded-full opacity-70"></div>
        <div className="absolute top-52 right-48 w-64 h-40 bg-teal-500 rounded-full opacity-60"></div>

        {/* Bottom left curved shape */}
        <div className="absolute -bottom-32 -left-40 w-[600px] h-[600px] bg-emerald-500 rounded-[40%] opacity-70"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Full Row - About Us Title and Description (Centered) */}
        <div
          className={`mb-16 text-center transition-all duration-1000 ${
            isVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-10"
          }`}
        >
          <h2 className="text-3xl md:text-7xl font-bold  text-emerald-700 mb-8">
            {t("about.title")}
          </h2>

          <div className="space-y-6 text-whiteleading-relaxed max-w-4xl mx-auto ">
            <p className="!font-bold text-[1rem]  ">
              {t("about.summary.text1")}
            </p>
            <p className="!font-bold text-[1rem]  ">
              {t("about.summary.text2")}
            </p>
          </div>
        </div>

        {/* Overlapping Cards Section */}
        <div className="relative max-w-6xl mx-auto min-h-0 lg:min-h-[700px]">
          {/* Goals Card (Left) */}
          <div
            className={`relative w-full lg:absolute lg:left-[12px] lg:top-0 lg:w-1/2 transition-all duration-1000 delay-300 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-4 md:p-8 md:p-10 shadow-2xl relative z-10">
              <h3 className="text-xl font-bold text-emerald-700 mb-3">
                {t("about.goals.title", t("goals.title"))}
              </h3>
              <div className="space-y-4 text-gray-700 text-sm leading-relaxed">
                <p className="font-semibold text-[1rem] ">
                  {t("about.goals.text1", t("goals.text1"))}
                </p>
                <p className="font-semibold text-[1rem] ">
                  {t("about.goals.text2", t("goals.text2"))}
                </p>
              </div>
            </div>
          </div>

          {/* Objectives & Target Groups Card (Right - Overlapping) */}
          <div
            className={`relative w-full mt-6 lg:mt-0 lg:absolute lg:right-[12px] lg:top-16 lg:w-1/2 transition-all duration-1000 delay-500 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            }`}
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-4   md:p-10 shadow-2xl relative z-20">
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-emerald-700 mb-3">
                    {t("about.objectives.title")}
                  </h3>
                  <ul className="space-y-2">
                    {objectives.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-[1rem]  leading-relaxed font-semibold">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-4">
                  <h3 className="text-xl font-bold text-emerald-700 mb-3">
                    {t("about.targetGroups.title")}
                  </h3>
                  <ul className="space-y-2">
                    {targetGroups.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <div className="w-2 h-2 bg-teal-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-[1rem]  leading-relaxed font-semibold">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
